import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";

/**
 * Offboarding survey.  Intercepts the chef's Unsubscribe click so we can
 * capture a structured churn category + optional detail text, which the
 * HQ Command Pulse dashboard aggregates by.
 *
 * Final CTA stays disabled until a reason is selected.
 */
const REASONS = [
  { key: "too_expensive",    label: "Too expensive" },
  { key: "missing_feature",  label: "Missing a specific feature I need" },
  { key: "too_complicated",  label: "Too complicated for my workflow" },
  { key: "other",            label: "Other (please tell us more)" },
];

export default function CancelSurveyModal({ open, onClose, onCancelled }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const otherSelected = reason === "other";
  const detailsRequiredForOther = otherSelected && details.trim().length < 3;
  const disabled = !reason || detailsRequiredForOther || submitting;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/billing/cancel-subscription`, {
        category: reason,
        details: details.trim() || null,
      });
      toast.success(
        t("cancelSurvey.success", {
          defaultValue:
            "Your subscription has been cancelled. We'll miss you — come back any time.",
        })
      );
      if (onCancelled) await onCancelled();
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t("cancelSurvey.failure", {
            defaultValue: "Couldn't cancel right now. Please try again or email admin@kritajnaventures.com.",
          })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      data-testid="cancel-survey-modal"
    >
      <div className="w-full max-w-md rounded-2xl border border-amber-500/40 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                {t("cancelSurvey.title", { defaultValue: "Before you go…" })}
              </h2>
              <p className="text-xs text-slate-400">
                {t("cancelSurvey.subtitle", {
                  defaultValue: "Tell us what pushed you to leave so we can earn you back.",
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200"
            data-testid="cancel-survey-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-2.5" data-testid="cancel-survey-reasons">
          {REASONS.map((r) => {
            const active = reason === r.key;
            return (
              <label
                key={r.key}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                  active
                    ? "border-amber-500 bg-amber-500/10"
                    : "border-slate-700 hover:border-slate-600 bg-slate-800/40"
                }`}
                data-testid={`cancel-survey-option-${r.key}`}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  checked={active}
                  onChange={() => setReason(r.key)}
                  className="accent-amber-500"
                  data-testid={`cancel-survey-radio-${r.key}`}
                />
                <span className={`text-sm ${active ? "text-white font-medium" : "text-slate-300"}`}>
                  {r.label}
                </span>
              </label>
            );
          })}

          {otherSelected && (
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder={t("cancelSurvey.otherPlaceholder", {
                defaultValue: "A few sentences would help us a lot…",
              })}
              className="mt-1 w-full rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="cancel-survey-other-details"
            />
          )}

          {!otherSelected && reason && (
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={t("cancelSurvey.detailsPlaceholder", {
                defaultValue: "Optional: anything specific that would have changed your mind?",
              })}
              className="mt-1 w-full rounded-lg bg-slate-950 border border-slate-700 text-xs text-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-600"
              data-testid="cancel-survey-details"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 bg-slate-950/40 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-400 hover:text-slate-200 px-3 py-2"
            data-testid="cancel-survey-keep"
          >
            {t("cancelSurvey.keep", { defaultValue: "Keep my subscription" })}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className={`inline-flex items-center gap-2 text-sm font-semibold rounded-lg px-4 py-2 transition-all ${
              disabled
                ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                : "bg-amber-500 text-slate-950 hover:bg-amber-400"
            }`}
            data-testid="cancel-survey-submit"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitting
              ? t("cancelSurvey.submitting", { defaultValue: "Cancelling…" })
              : t("cancelSurvey.confirm", { defaultValue: "Cancel subscription" })}
          </button>
        </div>
      </div>
    </div>
  );
}
